import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

// Create the theme context
const ThemeContext = createContext();

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme provider component
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light');
  const [loading, setLoading] = useState(true);
  const { user, isAuthenticated, token } = useAuth();

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Apply theme to document
  const applyTheme = (themeValue) => {
    const root = document.documentElement;

    // Remove existing theme classes
    root.classList.remove('light', 'dark', 'grey', 'system');

    if (themeValue === 'system') {
      // Use system preference
      root.classList.add('system');

      // Check system preference
      const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (systemDark) {
        root.classList.add('dark');
      }
    } else {
      // Use explicit theme
      root.classList.add(themeValue);
    }
  };

  // Load theme from localStorage or user preferences
  useEffect(() => {
    const loadTheme = async () => {
      try {
        // If user is authenticated, load from server
        if (isAuthenticated && user && token) {
          const response = await fetch(`${API_BASE}/preferences/theme`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            setTheme(data.theme);
            applyTheme(data.theme);
            // Also save to localStorage as backup
            localStorage.setItem('videbits-theme', data.theme);
          } else {
            throw new Error('Failed to load theme from server');
          }
        } else {
          // Fall back to localStorage
          const savedTheme = localStorage.getItem('videbits-theme');
          const initialTheme = savedTheme || 'light';
          setTheme(initialTheme);
          applyTheme(initialTheme);
        }
      } catch (error) {
        console.error('Error loading theme:', error);
        // Fall back to localStorage or default
        const savedTheme = localStorage.getItem('videbits-theme') || 'light';
        setTheme(savedTheme);
        applyTheme(savedTheme);
      } finally {
        setLoading(false);
      }
    };

    loadTheme();
  }, [isAuthenticated, user, token, API_BASE]);

  // Listen for system theme changes when using system theme
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = () => {
        applyTheme('system');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  // Change theme function
  const changeTheme = async (newTheme) => {
    if (!['light', 'dark', 'grey', 'system'].includes(newTheme)) {
      console.error('Invalid theme:', newTheme);
      return false;
    }

    try {
      setLoading(true);
      
      // Apply theme immediately for better UX
      setTheme(newTheme);
      applyTheme(newTheme);
      
      // Save to localStorage first
      localStorage.setItem('videbits-theme', newTheme);

      // If user is authenticated, save to server
      if (isAuthenticated && user && token) {
        const response = await fetch(`${API_BASE}/preferences/theme`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ theme: newTheme })
        });

        if (!response.ok) {
          throw new Error('Failed to save theme to server');
        }

        console.log(`Theme changed to ${newTheme} and saved to server`);
      } else {
        console.log(`Theme changed to ${newTheme} and saved locally`);
      }

      return true;
    } catch (error) {
      console.error('Error changing theme:', error);
      // Theme was already applied locally, so partial success
      return true;
    } finally {
      setLoading(false);
    }
  };

  // Get current effective theme (resolves 'system' to actual theme)
  const getEffectiveTheme = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  };

  // Check if dark mode is active
  const isDark = getEffectiveTheme() === 'dark';

  const value = {
    theme,
    effectiveTheme: getEffectiveTheme(),
    isDark,
    loading,
    changeTheme,
    applyTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};